import { CommonDialogs } from "@mcleod/common";
import { ChangeEvent, ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownItem, Snackbar, StepEvent, Toast } from "@mcleod/components";
import { Api, Model, ModelRow, Navigation, StringUtil } from "@mcleod/core";
import { LMECompanySettings, UsersResponsibilitySlideoutTrans } from "@mcleod/general";
import { QuoteSendEmail } from "@mcleod/powerbroker";
import { CarrierSelection } from "../CarrierSelection";
import { OrderPostHist, OrderPostHistProps } from "../OrderPostHist";
import { AutogenLayoutQuoteLTL } from "./autogen/AutogenLayoutQuoteLTL";
import { OrderRerateLogic } from "../OrderRerateLogic";

enum QuoteStep {
    QUOTE_ENTRY = 0,
    CARRIER_SELECTION = 1
}

export class QuoteLTL extends AutogenLayoutQuoteLTL {
    private reRate: boolean = false;
    private _p44Record: ModelRow;
    private _smc3Record: ModelRow;
    private _defaultRow: ModelRow;
    private p44RatingModes: DropdownItem[] = [
        new DropdownItem("LTL", "LTL"),
        new DropdownItem("VOL", "VOL"),
        new DropdownItem("LTV", "LTL and VOL")
    ];
    private smc3RatingModes: DropdownItem[] = [
        new DropdownItem("CON", "Contract"),
        new DropdownItem("DYN", "Dynamic"),
        new DropdownItem("VOM", "Volume"),
        new DropdownItem("CDP", "Contract and Dynamic"),
        new DropdownItem("CVP", "Contract and Volume"),
        new DropdownItem("DVP", "Dynamic and Volume"),
        new DropdownItem("CDV", "Contract, Dynamic, and Volume")
    ];

    public get p44Record(): ModelRow {
        return this._p44Record;
    }

    public set p44Record(value: ModelRow) {
        this._p44Record = value;
    }

    public get smc3Record(): ModelRow {
        return this._smc3Record;
    }

    public set smc3Record(value: ModelRow) {
        this._smc3Record = value;
    }

    public get defaultRow(): ModelRow {
        return this._defaultRow;
    }

    public set defaultRow(value: ModelRow) {
        this._defaultRow = value;
    }

    override async onLoad() {
        this.setupNestedDataSources();
        this.mainDataSource.doBeforeDataSourcePost = async () => {
            this.reRate = await OrderRerateLogic.promptForRerate(this.layoutQuoteEntry);
            return true;
        }
        this.p44Record = await Model.searchSingleRecord("lme/powerbroker/contract-management-control", { id: "P44" });
        this.smc3Record = await Model.searchSingleRecord("lme/powerbroker/contract-management-control", { id: "SLA" });
        this.setupQuoteStatusBadge();
        this.layoutQuoteEntry.textboxCustomerName.addChangeListener((event: ChangeEvent) => {
            const customerId = this.layoutQuoteEntry.textboxCustomerName.getFirstLookupModelData()?.get("id");
            if (customerId !== undefined) {
                Api.post("lme/powerbroker/inspect_customer_tariff", { customer_id: customerId }).then(async response => {
                    if (response?.data[0].tariff_has_smc3 && this.smc3Record?.getBoolean("is_active")) {
                        this.textboxSMC3RatingMode.visible = true;
                        const smc3RatingMode = response?.data[0].smc3_default_rating_mode != null ? response?.data[0].smc3_default_rating_mode : this.smc3RatingModes[0].value;
                        this.textboxSMC3RatingMode.setSelectedItemFromValue(smc3RatingMode);
                    } else
                        this.textboxSMC3RatingMode.visible = false;

                    if (response?.data[0].tariff_has_p44 && this.p44Record?.getBoolean("is_active")) {
                        this.textboxRatingMode.visible = true;
                        const p44RatingMode = response?.data[0].p44_default_rating_mode != null ? response?.data[0].p44_default_rating_mode : this.p44RatingModes[0].value;
                        this.textboxRatingMode.setSelectedItemFromValue(p44RatingMode);
                    } else
                        this.textboxRatingMode.visible = false;
                });
            }
            else if (customerId === undefined && StringUtil.isEmptyString(event.newValue))
                this.setupRatingModeDropdowns();
        });
        this.layoutQuoteEntry.layoutFreightItems.parentLayout = this;
    }

    sourceQuoteOrderAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode !== DataSourceMode.ADD) {
            this.title = "Quote Number " + this.activeRow.get("id");
            this.hideSuffix = true;
        }
        this.buttonQuoteHistory.visible = event.newMode === DataSourceMode.UPDATE;
        this.primaryButtonSetup();
        this.layoutQuoteEntry.setAllDataSourcesToMode(event.newMode);
        this.setupRatingModeDropdowns();
    }

    setupRatingModeDropdowns() {
        if (this.p44Record?.getBoolean("is_active")) {
            this.textboxRatingMode.visible = this.mainDataSource.mode === DataSourceMode.ADD
            if (this.textboxRatingMode.items == null)
                this.textboxRatingMode.items = this.p44RatingModes;
            if (this.textboxRatingMode.selectedItem == null || null == this.activeRow.data["customer_id"])
                this.textboxRatingMode.selectedItem = this.p44RatingModes[0];
        }
        if (this.smc3Record?.getBoolean("is_active")) {
            this.textboxSMC3RatingMode.visible = this.mainDataSource.mode === DataSourceMode.ADD
            if (this.textboxSMC3RatingMode.items == null)
                this.textboxSMC3RatingMode.items = this.smc3RatingModes;
            if (this.textboxSMC3RatingMode.selectedItem == null || null == this.activeRow.data["customer_id"])
                this.textboxSMC3RatingMode.selectedItem = this.smc3RatingModes[0];
        }
    }

    primaryButtonSetup() {
        if (this.dataheaderQuote.showSaveAndClose) {
            this.buttonSave.allowSaveAndClose = true;
            this.buttonSave.onClose = () => Navigation.navigateBack();
        }
        this.buttonSave.caption = this.mainDataSource.mode === DataSourceMode.ADD ? "Generate Quote" : this.dataheaderQuote.showSaveAndClose ? "Save & Close" : "Save";
        this.buttonSave.onSave = (postedRow) => this.dataheaderQuote?.onExecute?.(postedRow);
    }

    /** This is an event handler for the beforeStepListener event of stepperQuote.  */
    stepperQuoteBeforeStepListener(event: StepEvent) {
        switch (event.newIndex) {
            case QuoteStep.QUOTE_ENTRY:
                break;
            case QuoteStep.CARRIER_SELECTION:
                if (this.activeRow.isNull("id") || !this.stepQuoteEntry.validateSimple()) {
                    event.stop();
                    return;
                }
                if (OrderRerateLogic.shouldRerate(this.layoutQuoteEntry)) {
                    event.stop();
                    Snackbar.showWarningSnackbar("Quote has unsaved changes. Please save before viewing carrier rates.");
                    return;
                }
                this.textboxRatingMode.visible = false;
                this.textboxSMC3RatingMode.visible = false;
                this.layoutCarrierSelection.orderRow = this.activeRow;
                this.stepperQuote.selectedIndex = QuoteStep.CARRIER_SELECTION;
                break;
        }
        this.buttonEmail.visible = (event.newIndex === QuoteStep.CARRIER_SELECTION) &&
            this.layoutCarrierSelection.selectedForDispatchCarrier == null;
    }

    setupNestedDataSources() {
        this.layoutQuoteEntry.layoutQuoteStopTable.orderSource = this.mainDataSource;
        this.layoutQuoteEntry.layoutFreightItems.replaceMainDatasource(this.layoutQuoteEntry.sourceQuoteFreightGroupItem);
        this.layoutCarrierSelection.replaceMainDatasource(this.sourceQuoteBrltlBillingFreightGroup);
        this.layoutCarrierSelection.doAfterChoosingCarrier = async () => {}
    }

    buttonEmailOnClick(event) {
        QuoteSendEmail.show({
            sourceQuote: this.mainDataSource,
            doOnSlideoutClosed: (cancelled: boolean) => {
                if (!cancelled) {
                    Toast.showToast("Quote sent");
                }
            },
            isLtl: true
        });
    }

    sourceQuoteOrderAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.ADD) {
            Navigation.pseudoNavigateTo("/lme/dispatch/brltl/QuoteLTL?mode=update&key=" + this.activeRow.get("id"), null);
            const orderData: ModelRow = event.data;
            const stopSource = this.layoutQuoteEntry.sourceQuoteStop;
            stopSource.addAfterExecutionListener(() => {
                orderData.data["_child_links"] = null; //This contains stop, fgi's that have already posted. Causes duplicates
                orderData.resetOriginalData();
                this.stepperQuote.selectedIndex = QuoteStep.CARRIER_SELECTION;
            });
        }

    }

    // START - USERS Slideout
    /** This is an event handler for the onClick event of buttonUsers.  */
    buttonUsersOnClick(event: ClickEvent) {
        this.displayQuoteUsersSlideout();
    }

    displayQuoteUsersSlideout() {
        const urs = new UsersResponsibilitySlideoutTrans("lme/powerbroker/QuoteUsers", this.getQuoteUsersSlideoutTitle(), this.getQuoteUsersIdFilter(),
            this.getQuoteUsersSharedFieldNames(), this.sourceQuoteOrder, this.sourceResponsibleHist);
        urs.show();
    }

    private getQuoteUsersSlideoutTitle(): string {
        const name = this.sourceQuoteOrder.activeRow?.get("id");
        return "Quote Users" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getQuoteUsersIdFilter(): any {
        const id = this.sourceQuoteOrder.activeRow?.get("id");
        if (id != null)
            return { id: id };
        return null;
    }

    private getQuoteUsersSharedFieldNames(): string[] {
        return ["operations_user", "agent_payee_id", "entered_user_id", "billing_user_id"];
    }
    // END - USERS Slideout

    /** This is an event handler for the onClick event of buttonQuoteHistory.  */
    buttonQuoteHistoryOnClick(event: ClickEvent) {
        const props: OrderPostHistProps = {
            orderType: "QUOTE",
            orderId: this.activeRow.get("id"),
            movementId: null,
            isBilled: false
        }
        OrderPostHist.showInSlideout(props, DataSourceMode.UPDATE, this.buttonQuoteHistory);
    }

    setupQuoteStatusBadge() {
        this.mainDataSource.preventFocusFirstField = true;
        this.textboxQuoteStatus._input.style.height = "100%";
        this.textboxQuoteStatus._input.style.pointerEvents = "none";
        this.textboxQuoteStatus.addChangeListener(() => {
            if (this.stepperQuote.selectedIndex === QuoteStep.CARRIER_SELECTION) this.layoutCarrierSelection.getRates();
        });
        this.panelQuoteStatus.addClickListener(() => this.textboxQuoteStatus.showItemDropdown());
    }

    sourceQuoteBrltlBillingFreightGroupBeforeExecution(event: DataSourceExecutionEvent) {
        if (this.layoutCarrierSelection.defaultRow?.hasChanged()) {
            // In the case where we are going to prompt the user, we want to do so in the context of Carrier Selection.
            // So, do not navigate elsewhere
            this.buttonSave.onClose = () => {};
            const originalRatingMode = this.layoutCarrierSelection.defaultRow.originalData["rating_mode"];
            const originalP44DirectionMode = this.layoutCarrierSelection.defaultRow.originalData["p44_direction_override"];
            const originalPaymentTerms = this.layoutCarrierSelection.defaultRow.originalData["payment_terms"];
            const originalSmc3RatingMode = this.layoutCarrierSelection.defaultRow.originalData["smc3_rating_mode"];
            const originalSmc3PaymentTerms = this.layoutCarrierSelection.defaultRow.originalData["smc3_payment_terms"];
            const originalSmc3PayerOverride = this.layoutCarrierSelection.defaultRow.originalData["smc3_payer_override"];
            CommonDialogs.showYesNo("Updating Rating Controls may affect rates. Would you like to re-rate?", "Re-rate Order", {
                yesButtonCaption: "Yes", noButtonCaption: "No"
            }).then((response) => {
                if (response)
                    this.layoutCarrierSelection.buttonFindCarrierRates.clicked();
                else {
                    this.layoutCarrierSelection.updateDefaultRow(originalRatingMode, originalP44DirectionMode, originalPaymentTerms,
                        originalSmc3RatingMode, originalSmc3PaymentTerms, originalSmc3PayerOverride);
                    this.layoutCarrierSelection.textboxRatingMode.displayData(this.layoutCarrierSelection.defaultRow, null, 0);
                    this.layoutCarrierSelection.textboxP44DirectionOverride.displayData(this.layoutCarrierSelection.defaultRow, null, 0);
                    this.layoutCarrierSelection.textboxPaymentTerms.displayData(this.layoutCarrierSelection.defaultRow, null, 0);
                    this.layoutCarrierSelection.textboxSmc3RatingMode.displayData(this.layoutCarrierSelection.defaultRow, null, 0);
                    this.layoutCarrierSelection.textboxSmc3PaymentTerms.displayData(this.layoutCarrierSelection.defaultRow, null, 0);
                    this.layoutCarrierSelection.textboxSmc3PayerOverride.displayData(this.layoutCarrierSelection.defaultRow, null, 0);
                }
            });
        }
        else
            this.buttonSave.onClose = () => Navigation.navigateBack();
    }

    /** This is an event handler for the afterExecution event of sourceQuoteBrltlBillingFreightGroup. */
    async sourceQuoteBrltlBillingFreightGroupAfterExecution(event: DataSourceExecutionEvent) {
        if (this.reRate) {
            if (this.activeRow.get("smc3_rating_mode") !== event.dataSource.activeRow.get("smc3_rating_mode") ||
                this.activeRow.get("rating_mode") !== event.dataSource.activeRow.get("rating_mode")) {
                if (this.activeRow.get("smc3_rating_mode") !== event.dataSource.activeRow.get("smc3_rating_mode"))
                    event.dataSource.activeRow.set("smc3_rating_mode", this.activeRow.get("smc3_rating_mode"));
                if (this.activeRow.get("rating_mode") !== event.dataSource.activeRow.get("rating_mode"))
                    event.dataSource.activeRow.set("rating_mode", this.activeRow.get("rating_mode"));
                await event.dataSource.post()
            }
            else {
                this.layoutCarrierSelection.buttonFindCarrierRates.busy = true;
                await CarrierSelection.findCarrierRates(this.activeRow.get("id")).finally(() => {
                    this.layoutCarrierSelection.mainDataSource.search({ order_id: this.activeRow.get("id")})
                    this.reRate = false;
                    this.layoutCarrierSelection.buttonFindCarrierRates.busy = false;
                });
            }
        }

    }

    /** This is an event handler for the beforeExecution event of sourceQuoteOrder. */
    sourceQuoteOrderBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() !== DataSourceAction.SEARCH && LMECompanySettings.getSingleton().isBrokerageLtl) {
            this.validateStopsHaveZipCodes(event);
        }
    }

    private validateStopsHaveZipCodes(event: DataSourceExecutionEvent) {
        const stops: ModelRow[] = this.layoutQuoteEntry.sourceQuoteStop?.data
        for (const stopRow of stops) {
            const zipCode = stopRow?.get("zip_code");
            if (StringUtil.isEmptyString(zipCode)) {
                CommonDialogs.showError("Zip codes must be provided for locations");
                event.stop();
                break;
            }
        }
    }
}
